<template lang="pug">
form.section#paymentForm(v-on:submit.prevent="submit")
  .field
    .control.is-expanded
      .select.is-fullwidth(v-bind:class="[$v.professional.$error ? 'is-danger' : 'is-success']")
        select(ref="focus" v-model.number="$v.professional.$model")
          option(value="0") Please select a trainer
          option(v-bind:value="professional.id" v-for="professional in apiProfessionals") {{ professional.name }}
    .help.is-danger(v-show="$v.professional.$error && !$v.professional.minValue") Please select a trainer
  template(v-if="$v.professional.$model")
    .field(v-if="tagline")
      .control
        span {{ tagline }}
    .field
      .control.is-expanded
        .select.is-fullwidth(v-bind:class="[$v.location.$error ? 'is-danger' : 'is-success']")
          select(ref="focus" v-model.number="$v.location.$model")
            option(value="0") Please select a location
            option(v-bind:value="location.id" v-for="location in locations") {{ location.name }}
      .help.is-danger(v-show="$v.location.$error && !$v.location.minValue") Please select a location
    .field
      .control
        b-datepicker(icon="calendar" placeholder="Click to select date" v-model="$v.bookingStartDate.$model")
    .field
      .label From:
    .field(v-if="!startHour.disabled" v-for="startHour in startHours")
      .control
        b-radio(v-bind:disabled="startHour.disabled" v-bind:native-value="startHour.value" v-model="$v.bookingStartHour.$model") {{ startHour.label }}
    .field(v-if="noAvailableStartHours")
      .control
        .icon
          i.far.fa-frown
        span Nothing available.
    template(v-if="bookingStartHour")
      .field
        .label To:
      .field(v-if="!endHour.disabled" v-for="endHour in endHours")
        .control
          b-radio(v-bind:disabled="endHour.disabled" v-bind:native-value="endHour.value" v-model="$v.bookingEndHour.$model") {{ endHour.label }}
      .field(v-if="noAvailableEndHours")
        .control
          .icon
            i.far.fa-frown
          span Nothing available.
      template(v-if="bookingEndHour")
        template(v-if="paymentNeeded")
          template(v-if="hasCredits")
            .field
              .control
                span {{ hasCreditsMessage }}
          template(v-if="!hasCredits")
            .field
              .label Purchase option:
            .field(v-for="price in availablePrices")
              .control
                b-radio(v-bind:native-value="price.credits" v-model="$v.price.$model") {{ price.label }}
            template(v-if="transactionNeeded")
              template(v-if="!hasVoucher")
                .field
                  .control
                    button.button.has-text-weight-semibold.is-primary(type="button" v-on:click="enterVoucherCodeForm") Enter Voucher Code
              template(v-if="hasToken")
                .field
                  .control
                    span {{ debitMessage }}
                    | &nbsp;
                    a(v-on:click="clickForgetThisCard") Forget this card.
              template(v-if="hasCardCvcRequired")
                .field
                  .control
                    span We need to revalidate your card.
                .field
                  .control.is-expanded
                    the-mask.input(autocomplete="cc-csc" mask="###" placeholder="CVC" type="tel" v-bind:class="[$v.cardCvcRequired.$error ? 'is-danger' : 'is-success']" v-model.trim="$v.cardCvcRequired.$model")
                  .help.is-danger(v-show="$v.cardCvcRequired.$error && !$v.cardCvcRequired.minLength") CVC is too short.
                  .help.is-danger(v-show="$v.cardCvcRequired.$error && !$v.cardCvcRequired.required") CVC is required.
              template(v-if="!hasToken")
                .field
                  .control.is-expanded
                    input.input(autocomplete="cc-name" data-worldpay="name" placeholder="Cardholder Name" v-bind:class="[$v.cardName.$error ? 'is-danger' : 'is-success']" v-model="$v.cardName.$model")
                  .help.is-danger(v-show="$v.cardName.$error && !$v.cardName.invalidCardName") Cardholder name is invalid.
                  .help.is-danger(v-show="$v.cardName.$error && !$v.cardName.required") Cardholder name is required.
                .field
                  .control.is-expanded
                    the-mask.input(autocomplete="cc-number" data-worldpay="number" mask="#### #### #### ####" placeholder="Card Number" type="tel" v-bind:class="[$v.cardNumber.$error ? 'is-danger' : 'is-success']" v-model.trim="$v.cardNumber.$model")
                  .help.is-danger(v-show="$v.cardNumber.$error && !$v.cardNumber.minLength") Card number is too short.
                  .help.is-danger(v-show="$v.cardNumber.$error && !$v.cardNumber.required") Card number is required.
                .field.is-grouped
                  .control
                    .select
                      select(data-worldpay="exp-month" v-model.number="$v.cardExpiryMonth.$model")
                        option(v-bind:value="month.value" v-for="month in months") {{ month.label }}
                  .control
                    .select
                      select(data-worldpay="exp-year" v-model.number="$v.cardExpiryYear.$model")
                        option(v-bind:value="year.value" v-for="year in years") {{ year.label }}
                .field
                  .control.is-expanded
                    the-mask.input(autocomplete="cc-csc" data-worldpay="cvc" mask="###" placeholder="CVC" type="tel" v-bind:class="[$v.cardCvc.$error ? 'is-danger' : 'is-success']" v-model.trim="$v.cardCvc.$model")
                  .help.is-danger(v-show="$v.cardCvc.$error && !$v.cardCvc.minLength") CVC is too short.
                  .help.is-danger(v-show="$v.cardCvc.$error && !$v.cardCvc.required") CVC is required.
                .field
                  .control
                    span {{ debitMessage }}
  .field
    .control
      button.button.is-fullwidth.is-primary(type="submit" v-bind:disabled="$v.$invalid") Place Booking
</template>

<script>
import { addMinutes, areRangesOverlapping, differenceInHours, format, getMonth, getYear, isAfter, isBefore, isEqual, isWithinRange, parse, startOfDay } from 'date-fns'
import Vue from 'vue'
import { helpers, minLength, minValue, required } from 'vuelidate/lib/validators'

import Constants from '@/constants'

import EnterVoucherCodeForm from '@/components/EnterVoucherCodeForm.vue'

import AlertMixin from '@/mixins/AlertMixin.vue'
import ApiMixin from '@/mixins/ApiMixin.vue'
import DeviceMixin from '@/mixins/DeviceMixin.vue'

const validCardName = helpers.regex('invalidCardName', /^[a-zA-Z]+[\s]{1}[a-zA-Z]+$/)

const CREATE_BOOKING_DURATION_CLIENT = 60
const CREATE_BOOKING_DURATION_MINIMUM = 15
const CREATE_BOOKING_DURATION_SLOTS = 24 * 60 / CREATE_BOOKING_DURATION_MINIMUM

export default {
  mixins: [
    AlertMixin,
    ApiMixin,
    DeviceMixin
  ],
  data () {
    return {
      bookingEndHour: null,
      bookingStartDate: null,
      bookingStartHour: null,
      cardCvc: null,
      cardCvcRequired: null,
      cardExpiryMonth: null,
      cardExpiryYear: null,
      cardName: null,
      cardNumber: null,
      client: null,
      initialLocation: 0,
      initialProfessional: 0,
      location: 0,
      price: null,
      professional: 0
    }
  },
  computed: {
    availableCredits () {
      return this.apiClientCredits.filter((credit) => {
        return credit.status === Constants.STATUS_AVAILABLE && credit.transaction.client_id === this.client && credit.transaction.professional_id === this.professional
      })
    },
    availablePrices () {
      let professional = null
      if (this.professional) {
        professional = this.apiProfessionals.find((professional) => professional.id === this.professional)
      }
      return this.prices.filter((price) => {
        return professional && professional.prices[0].General[`price${price.credits}`] !== 0 && (price.amountInPennies !== 0 || price.voucherName !== null)
      })
    },
    bookingEnd () {
      return addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * this.bookingEndHour)
    },
    bookingStart () {
      return addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * this.bookingStartHour)
    },
    debitMessage () {
      if (this.prices && this.price) {
        const price = this.prices.find((price) => price.credits === this.price)
        let debitMessage = `We will debit £${price.amountInPounds} from your card`
        if (this.hasToken) {
          debitMessage = debitMessage.concat(` (${this.maskedCardNumber}) for this booking. `)
          if (price.credits === 1) {
            if (differenceInHours(this.bookingStart, new Date()) < 24) {
              debitMessage = debitMessage.concat('Payment will be taken when the trainer accepts the booking.')
            } else {
              debitMessage = debitMessage.concat('Payment will be taken 24 hours before the booking is due to take place.')
            }
          } else {
            debitMessage = debitMessage.concat('Payment will be taken immediately for this booking.')
          }
        } else {
          debitMessage = debitMessage.concat(' immediately for this booking.')
        }
        return debitMessage
      }
      return null
    },
    endHours () {
      return Array(CREATE_BOOKING_DURATION_SLOTS).fill({}).map((_, index) => {
        const bookingEnd = addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * index)
        const bookingStart = addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * this.bookingStartHour)
        const conflictedProfessionalBooking = this.apiProfessionalBookings.findIndex((booking) => {
          if (booking.booking_status === Constants.STATUS_ACCEPTED || booking.booking_status === Constants.STATUS_REQUESTED) {
            if (isBefore(bookingStart, bookingEnd)) {
              return areRangesOverlapping(bookingStart, bookingEnd, this.parsedDateTime(booking.booking_start), this.parsedDateTime(booking.booking_end)) || (isBefore(bookingStart, this.parsedDateTime(booking.booking_start)) && isAfter(bookingEnd, this.parsedDateTime(booking.booking_start)))
            }
          }
        }) > -1
        const conflictedSlot = this.apiProfessionalSlots.findIndex((slot) => {
          return isWithinRange(bookingStart, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time)) && isWithinRange(bookingEnd, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time))
        }) === -1
        const conflictedStart = index <= this.bookingStartHour
        const conflictedEnd = this.client !== 1 && !isEqual(bookingEnd, addMinutes(bookingStart, CREATE_BOOKING_DURATION_CLIENT))
        return {
          disabled: conflictedProfessionalBooking || conflictedSlot || conflictedStart || conflictedEnd,
          label: this.formattedDateTime(bookingEnd).substring(11, 16),
          value: index
        }
      })
    },
    hasCardCvcRequired () {
      return this.paymentNeeded && !this.hasCredits && this.transactionNeeded && this.hasToken && this.price > 1
    },
    hasCredits () {
      return this.numberOfAvailableCredits > 0
    },
    hasCreditsMessage () {
      return `You have ${this.numberOfAvailableCredits} credit${this.numberOfAvailableCredits > 1 ? 's' : ''} with ${this.professionalName}, ${this.numberOfAvailableCredits > 1 ? 'one of' : ''} which will be used for this booking.`
    },
    hasToken () {
      return this.token !== null
    },
    hasVoucher () {
      if (this.prices && this.price) {
        const price = this.prices.find((price) => price.credits === this.price)
        return price.voucherName !== null
      }
      return false
    },
    locations () {
      if (this.professional === 0) {
        return []
      }
      const professional = this.apiProfessionals.find((professional) => professional.id === this.professional)
      return this.apiLocations.filter((location) => professional.locations.map((location) => location.id).includes(location.id))
    },
    maskedCardNumber () {
      const token = this.apiClientTokens[0]
      return token ? token.paymentMethod.maskedCardNumber : null
    },
    months () {
      return Array(12).fill({}).map((_, index) => {
        return {
          label: (index + 1 + '').padStart(2, '0'),
          value: index + 1
        }
      })
    },
    noAvailableEndHours () {
      return this.endHours.filter((endHour) => {
        return !endHour.disabled
      }).length === 0
    },
    noAvailableStartHours () {
      return this.startHours.filter((startHour) => {
        return !startHour.disabled
      }).length === 0
    },
    numberOfAvailableCredits () {
      return this.availableCredits.length
    },
    paymentNeeded () {
      return this.client !== 1
    },
    prices () {
      return this.apiProfessionalTransactionPrices.map((price) => {
        return {
          amountInPennies: price.transaction_price * 100,
          amountInPounds: price.transaction_price,
          credits: price.credits,
          label: `${price.credits} credit${price.credits > 1 ? 's' : ''} for £${price.transaction_price}`.concat(price.voucher_name ? ` (applying voucher ${price.voucher_name})` : ''),
          voucherName: price.voucher_name
        }
      })
    },
    professionalName () {
      const professional = this.apiProfessionals.find((professional) => {
        return professional.id === this.professional
      })
      return professional ? professional.name : null
    },
    startHours () {
      const bookingStartMin = addMinutes(new Date(), Constants.CLIENT_BOOKING_EXCLUSION_PERIOD)
      return Array(CREATE_BOOKING_DURATION_SLOTS).fill({}).map((_, index) => {
        const bookingStart = addMinutes(this.bookingStartDate, CREATE_BOOKING_DURATION_MINIMUM * (index + 0))
        const nearestBookingEnd = this.client === Constants.EXTERNAL_CLIENT_ID ? addMinutes(bookingStart, CREATE_BOOKING_DURATION_MINIMUM) : addMinutes(bookingStart, CREATE_BOOKING_DURATION_CLIENT)
        const conflictedClientBooking = this.apiClientBookings.findIndex((booking) => {
          if (booking.booking_status !== Constants.STATUS_CANCELLED) {
            return areRangesOverlapping(bookingStart, nearestBookingEnd, this.parsedDateTime(booking.booking_start), this.parsedDateTime(booking.booking_end))
          }
        }) > -1
        const conflictedProfessionalBooking = this.apiProfessionalBookings.findIndex((booking) => {
          if (booking.booking_status === Constants.STATUS_ACCEPTED) {
            return areRangesOverlapping(bookingStart, nearestBookingEnd, this.parsedDateTime(booking.booking_start), this.parsedDateTime(booking.booking_end))
          }
        }) > -1
        const conflictedSlot = this.apiProfessionalSlots.findIndex((slot) => {
          return isWithinRange(bookingStart, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time)) && isWithinRange(nearestBookingEnd, this.parsedDateTime(slot.start_time), this.parsedDateTime(slot.end_time))
        }) === -1
        const conflictedStart = bookingStart < bookingStartMin
        return {
          disabled: conflictedClientBooking || conflictedProfessionalBooking || conflictedSlot || conflictedStart,
          label: this.formattedDateTime(bookingStart).substring(11, 16),
          value: index
        }
      })
    },
    tagline () {
      if (this.professional === 0) {
        return null
      }
      const professional = this.apiProfessionals.find((professional) => professional.id === this.professional)
      return professional.tagline ? decodeURIComponent(professional.tagline) : null
    },
    token () {
      const token = this.tokens[0]
      return token ? token.token : null
    },
    tokens () {
      return this.apiClientTokens.filter((token) => {
        return token.reusable
      })
    },
    transactionNeeded () {
      if (this.prices && this.price) {
        const price = this.prices.find((price) => price.credits === this.price)
        return price.amountInPennies > 0
      }
      return false
    },
    years () {
      return Array(7).fill({}).map((_, index) => {
        const year = getYear(new Date())
        return {
          label: index + year,
          value: index + year
        }
      })
    }
  },
  watch: {
    async apiMixinInitialised () {
      await this.initialiseAddClientBooking(0, 0)
    },
    availablePrices () {
      if (this.availablePrices.length === 1) {
        this.price = this.availablePrices[0].credits
      }
    },
    bookingStartDate () {
      this.initialiseBookingStartHour()
    },
    bookingStartHour () {
      this.initialiseBookingEndHour()
    },
    endHours () {
      const availableEndHours = this.endHours.filter((endHour) => {
        return !endHour.disabled
      })
      if (availableEndHours.length === 1) {
        this.bookingEndHour = availableEndHours[0].value
      }
    },
    async professional () {
      if (this.professional) {
        this.location = this.locations.length === 1 ? this.locations[0].id : this.professional === this.initialProfessional ? this.initialLocation : 0
        this.initialiseBookingStartDate()
        this.initialisePrice()
        await this.apiGetProfessionalBookings(this.professional)
        await this.apiGetProfessionalSlots(this.professional)
        await this.apiGetProfessionalTransactionPrices(this.professional, this.client)
      } else {
        this.location = 0
      }
    }
  },
  beforeDestroy () {
    this.$root.$off('initialiseAddBooking')
    this.$root.$off('initialiseOverlappingBooking')
  },
  async mounted () {
    this.$root.$on('initialiseAddBooking', async (locationId, professionalId) => {
      this.initialLocation = locationId
      this.initialProfessional = professionalId
      await this.initialiseAddClientBooking(locationId, professionalId)
    })
    this.$root.$on('initialiseOverlappingBooking', async () => {
      await this.initialiseOverlappingBooking()
    })
  },
  methods: {
    clickForgetThisCard () {
      this.$buefy.dialog.confirm({
        cancelText: 'No',
        confirmText: 'Yes',
        focusOn: 'cancel',
        hasIcon: true,
        message: 'Are you sure you want to forget this card?<br><br>Note: make sure to save a new card to allow the successful payment of upcoming bookings not already paid for.',
        onConfirm: () => this.forgetThisCard(),
        title: 'Forget this Card',
        type: 'is-danger'
      })
    },
    enterVoucherCodeForm () {
      this.$buefy.modal.open({
        component: EnterVoucherCodeForm,
        hasModalCard: true,
        parent: this,
        props: {
          impl: this.enterVoucherCodeFormImpl,
          initialName: '',
          noun: 'Voucher Code',
          verbBase: 'Enter',
          verbGerund: 'Entering'
        }
      })
    },
    async enterVoucherCodeFormImpl (name) {
      await this.apiCreateClientVoucher(this.client, name)
      await this.apiGetProfessionalTransactionPrices(this.professional, this.client)
    },
    forgetThisCard () {
      this.apiDeleteClientTokens(this.apiUserId)
    },
    formattedDateTime (dateTime) {
      return format(dateTime, 'YYYY-MM-DD HH:mm:ss')
    },
    async initialiseAddClientBooking (locationId, professionalId) {
      this.deviceSetFocus()
      this.initialiseBookingStartDate()
      this.initialiseBookingStartHour()
      this.initialiseCardCvc()
      this.initialiseCardCvcRequired()
      this.initialiseCardExpiryMonth()
      this.initialiseCardExpiryYear()
      this.initialiseCardName()
      this.initialiseCardNumber()
      this.initialiseClient()
      this.initialisePrice()
      this.initialiseProfessional(locationId, professionalId)
      this.initialiseValidation()
      this.initialiseWorldpay()
    },
    initialiseBookingEndHour () {
      this.bookingEndHour = null
    },
    initialiseBookingStartDate () {
      this.bookingStartDate = startOfDay(new Date())
    },
    initialiseBookingStartHour () {
      this.bookingStartHour = null
    },
    initialiseCardCvc () {
      this.cardCvc = null
    },
    initialiseCardCvcRequired () {
      this.cardCvcRequired = null
    },
    initialiseCardExpiryMonth () {
      this.cardExpiryMonth = getMonth(new Date()) + 1
    },
    initialiseCardExpiryYear () {
      this.cardExpiryYear = getYear(new Date())
    },
    initialiseCardName () {
      this.cardName = null
    },
    initialiseCardNumber () {
      this.cardNumber = null
    },
    async initialiseClient () {
      this.client = this.apiUserId
      await this.apiGetClientCredits(this.client)
      await this.apiGetClientTokens(this.client)
    },
    async initialiseOverlappingBooking () {
      await this.apiGetProfessionalBookings(this.professional)
      this.initialiseBookingStartHour()
    },
    initialisePrice () {
      this.price = null
    },
    initialiseProfessional (locationId, professionalId) {
      this.professional = professionalId
      this.location = locationId
    },
    initialiseValidation () {
      this.$v.$reset()
    },
    initialiseWorldpay () {
      Vue.nextTick(() => {
        window.Worldpay.useOwnForm({
          callback: this.worldpayCallback,
          clientKey: Constants.WORLDPAY,
          form: document.getElementById('paymentForm'),
          reusable: true
        })
      })
    },
    parsedDateTime (dateTime) {
      return parse(dateTime, 'YYYY-MM-DD HH:mm:ss')
    },
    async submit (_, token = null) {
      if (!this.paymentNeeded || this.hasCredits || !this.transactionNeeded || token) {
        if (this.paymentNeeded && !this.hasCredits && (this.hasVoucher || (token && this.price !== 1))) {
          const price = this.prices.find((price) => price.credits === this.price)
          await this.apiCreateProfessionalTransaction(price.amountInPennies, this.client, this.cardCvcRequired, price.credits, this.professional, token, price.voucherName)
        }
        await this.apiCreateBooking(this.formattedDateTime(this.bookingEnd), this.formattedDateTime(this.bookingStart), this.client, this.location, this.professional)
      }
    },
    async worldpayCallback (status, response) {
      if (this.paymentNeeded && this.transactionNeeded) {
        if (!this.hasCredits && !this.hasToken) {
          if (status === 200) {
            if (response.token) {
              await this.apiCreateClientToken(this.client, response.token)
              this.submit(null, response.token)
            }
          } else {
            this.alertDisplayFailure(`${response.error.message[0]}.`)
          }
        } else if (!this.hasCredits && this.hasToken) {
          this.submit(null, this.token)
        }
      }
    }
  },
  validations () {
    const cardCvcRequiredValidations = {
      cardCvcRequired: {
        minLength: minLength(3),
        required
      }
    }
    const cardValidations = {
      cardCvc: {
        minLength: minLength(3),
        required
      },
      cardExpiryMonth: {},
      cardExpiryYear: {},
      cardName: {
        minLength: minLength(3),
        required,
        validCardName
      },
      cardNumber: {
        minLength: minLength(16),
        required
      }
    }
    const priceValidations = {
      price: {
        required
      }
    }
    let validations = {
      bookingEndHour: {
        required
      },
      bookingStartDate: {
        required
      },
      bookingStartHour: {
        required
      },
      client: {
        minValue: minValue(1)
      },
      location: {
        minValue: minValue(1)
      },
      professional: {
        minValue: minValue(1)
      }
    }
    if (this.paymentNeeded && !this.hasCredits && this.transactionNeeded && !this.hasToken) {
      validations = { ...validations, ...cardValidations }
    }
    if (!this.hasCredits) {
      validations = { ...validations, ...priceValidations }
    }
    if (this.hasCardCvcRequired) {
      validations = { ...validations, ...cardCvcRequiredValidations }
    }
    return validations
  }
}
</script>

<style lang="sass" scoped>
.has-margin-bottom
  margin-bottom: 12px
</style>
